import {useEffect,useState} from 'react';
import {authHeaders,fetchImpl} from '../lib/api';
import {env} from '../lib/env';
import {useT} from '../lib/i18n';
import type {AddressHint,AddressSuggestion} from '../lib/address-suggestion';

type Props={
 id:string;
 value:string;
 onChange:(value:string)=>void;
 onSelect:(item:AddressSuggestion)=>void;
 disabled?:boolean;
 className?:string;
 maxLength?:number;
 placeholder?:string;
 autoFocus?:boolean;
 onEscape?:()=>void;
};

/** One staff address search for every CRM editor. The API owns the provider key; typing streams
 * hints (nothing stored), the pick resolves one address into the structured parts the record saves.
 * Callers may still enter a line manually if search is unavailable. */
export function AddressAutocomplete({id,value,onChange,onSelect,disabled=false,className='input mt-1 w-full',maxLength=500,placeholder,autoFocus,onEscape}:Props){
 const {t}=useT();
 const [items,setItems]=useState<AddressHint[]>([]),[selectedLabel,setSelectedLabel]=useState<string|null>(null),[unavailable,setUnavailable]=useState(false),[resolving,setResolving]=useState(false);
 useEffect(()=>{
  if(disabled||resolving||value===selectedLabel||value.trim().length<3){setItems([]);return;}
  const abort=new AbortController();
  const timer=setTimeout(async()=>{try{
   const res=await fetchImpl(`${env.apiBase}/api/address-suggestions?q=${encodeURIComponent(value.trim())}`,{headers:await authHeaders(),signal:abort.signal});
   if(!res.ok)throw Error();
   const body=await res.json() as {data:AddressHint[]};
   if(!abort.signal.aborted){setItems(body.data);setUnavailable(false);}
  }catch{if(!abort.signal.aborted){setItems([]);setUnavailable(true);}}},350);
  return()=>{clearTimeout(timer);abort.abort();};
 },[disabled,resolving,selectedLabel,value]);
 async function pick(item:AddressHint){
  setItems([]);setResolving(true);
  try{
   const res=await fetchImpl(`${env.apiBase}/api/address-suggestions/${encodeURIComponent(item.id)}`,{headers:await authHeaders()});
   if(!res.ok)throw Error();
   const body=await res.json() as {data:AddressSuggestion};
   setSelectedLabel(body.data.label);onChange(body.data.label);onSelect(body.data);setUnavailable(false);
  }catch{
   // The pick could not be resolved: keep the line the person chose so it can still be saved by hand.
   setSelectedLabel(item.label);onChange(item.label);setUnavailable(true);
  }finally{setResolving(false);}
 }
 return <>
  <input id={id} autoFocus={autoFocus} className={className} value={value} disabled={disabled||resolving} maxLength={maxLength} placeholder={placeholder} autoComplete="off" aria-busy={resolving} onChange={e=>{setSelectedLabel(null);onChange(e.target.value);}} onKeyDown={e=>{if(e.key==='Escape'&&!disabled)onEscape?.();}}/>
  {items.length>0&&<ul aria-label={t('job.address.suggestions')} className="mt-1 max-h-56 overflow-y-auto rounded border bg-popover shadow-md">{items.map(item=><li key={item.id}><button type="button" className="w-full p-2 text-left hover:bg-muted" onClick={()=>void pick(item)}>{item.label}</button></li>)}<li className="px-2 py-1 text-micro text-muted-foreground">{t('address.attribution')}</li></ul>}
  {resolving&&<p role="status" className="mt-1 text-micro text-muted-foreground">{t('address.resolving')}</p>}
  {unavailable&&<p role="status" className="mt-1 text-micro text-muted-foreground">{t('job.address.manual')}</p>}
 </>;
}
